import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, currentPushStatus, subscribeToPush, unsubscribeFromPush } from "@/lib/push";

export function PushToggle({ variant = "card" }) {
  const [status, setStatus] = useState("default");
  const [busy, setBusy] = useState(false);

  useEffect(() => { currentPushStatus().then(setStatus); }, []);

  if (!pushSupported() || status === "unsupported") return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setStatus("subscribed");
      toast.success("Order alerts enabled 🔔 — we'll notify you at every step.");
    } catch (e) {
      if (e.message === "denied") toast.error("Notifications are blocked. Enable them in your browser settings.");
      else toast.error("Couldn't enable alerts. Please try again.");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try { await unsubscribeFromPush(); setStatus("default"); toast("Order alerts turned off"); }
    catch { toast.error("Couldn't turn off alerts"); }
    finally { setBusy(false); }
  };

  const subscribed = status === "subscribed";
  const denied = status === "denied";

  if (variant === "inline") {
    return (
      <button data-testid="push-toggle-btn" disabled={busy || denied}
        onClick={subscribed ? disable : enable}
        className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold transition-colors disabled:opacity-60 ${subscribed ? "bg-secondary text-ink hover:bg-secondary/70" : "bg-coral text-white hover:bg-white hover:text-ink"}`}>
        {busy ? <Loader2 size={18} className="animate-spin" /> : subscribed ? <BellOff size={18} /> : <Bell size={18} />}
        {denied ? "Notifications blocked" : subscribed ? "Turn off order alerts" : "Enable order alerts"}
      </button>
    );
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-5 flex items-center gap-4" data-testid="push-toggle-card">
      <span className={`h-11 w-11 rounded-xl grid place-items-center shrink-0 ${subscribed ? "bg-emerald/15 text-emerald" : "bg-coral/15 text-coral"}`}>
        {subscribed ? <Bell size={22} /> : <BellOff size={22} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold">Order alerts</div>
        <div className="text-xs text-muted-foreground">{denied ? "Blocked in your browser settings — re-enable to get updates." : subscribed ? "On — you'll get a push at every delivery step." : "Get a push notification as your order moves to delivered."}</div>
      </div>
      <button data-testid="push-toggle-btn" disabled={busy || denied}
        onClick={subscribed ? disable : enable}
        className={`shrink-0 inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${subscribed ? "bg-secondary text-ink hover:bg-secondary/70" : "bg-ink text-white hover:bg-coral"}`}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : subscribed ? "Turn off" : "Enable"}
      </button>
    </div>
  );
}
